import { Stack } from "@mantine/core"

import { CUSTOMISATION_CATEGORIES } from "@tfg/types/constants/communication-studio.constant"
import { AGECResponses } from "@tfg/types/endpoints/agec"
import {
  ProductSheetComponent,
  ComponentContentSchema,
  ComponentContent,
  COMPONENT_TYPES,
} from "@tfg/types/schemas/communication-studio/components"
import {
  paddingBlockCSSVariable,
  paddingInlineCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"
import { ScopedConfigurationsResponse } from "@tfg/types/types/communication-studio/configurations.types"

import { ProductAttributes } from "../../ProductSheetPageContainer/page-components/ProductAttributes"
import { TextParagraph } from "../../ProductSheetPageContainer/page-components/TextParagraph"
import { FooterAGEC, PoweredBy } from "../Footer"
import { PackagingInfo } from "../ui/PackagingInfo"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"

import { ActivityList } from "./ActivityList"
import { BulletedList } from "./BulletedList"
import { DetailedProductComposition } from "./DetailedProductComposition"
import { EcoScore } from "./EcoScore"
import { GenericTitle } from "./GenericTitle"
import { Logo } from "./Logo"
import { OrganizationTitle } from "./OrganizationTitle"
import { ProductImageComponent } from "./ProductImageComponent"
import { RecycledFiberAndMicroPlastics } from "./RecyledFibersAndMicroPlastics"
import TextParagraphV2Renderer from "./TextParagraphV2"

type Props = {
  component: ProductSheetComponent
  agecData: AGECResponses.GenericAgecData
  configuration: ScopedConfigurationsResponse
}

export function isComponentContent(
  content: Record<string, unknown>
): content is ComponentContent {
  const result = ComponentContentSchema.safeParse(content)
  if (!result.success) {
    console.error(result.error)
    return false
  } else {
    return true
  }
}

export function ProductSheetComponentRenderer({
  component,
  agecData,
  configuration,
}: Props) {
  if (!component || !component.componentType) {
    return null
  }
  switch (component.componentType.toUpperCase()) {
    case COMPONENT_TYPES.enum.TEXT_PARAGRAPH:
      if (!isComponentContent(component.content) || !component.content) {
        return null
      }
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <TextParagraph content={component.content} key={component.id} />
        </Stack>
      )
    case COMPONENT_TYPES.enum.ACTIVITY_LIST: {
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <ActivityList
            agecData={agecData}
            {...DesignStudioParametersToolkit.spreadConfigurationParameters(
              CUSTOMISATION_CATEGORIES.ACTIVITIES,
              configuration
            )}
          />
        </Stack>
      )
    }
    case COMPONENT_TYPES.enum.RECYCLED_MATERIALS_INFO:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <RecycledFiberAndMicroPlastics agecData={agecData} />
        </Stack>
      )
    case COMPONENT_TYPES.enum.PACKAGING_INFO:
      return agecData.withPrimaryPackaging &&
        agecData.primaryPackagingRecycledPercentage !== null &&
        agecData.primaryPackagingRecycledPercentage > 0 ? (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <PackagingInfo agecData={agecData} />
        </Stack>
      ) : null
    case COMPONENT_TYPES.enum.TRIMAN_FOOTER:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <FooterAGEC
            agecData={agecData}
            {...DesignStudioParametersToolkit.spreadConfigurationParameters(
              CUSTOMISATION_CATEGORIES.FOOTER,
              configuration
            )}
          />
        </Stack>
      )
    case COMPONENT_TYPES.enum.POWERED_BY:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <PoweredBy />
        </Stack>
      )
    case COMPONENT_TYPES.enum.ECOSCORE: {
      const { ecoScoreDataSource, ecoScoreDisplayMode } =
        DesignStudioParametersToolkit.spreadContentFilteringConfiguration(
          configuration
        )
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <EcoScore
            agecData={agecData}
            ecoScoreDataSource={ecoScoreDataSource ?? null}
            ecoScoreDisplayMode={ecoScoreDisplayMode}
          />
        </Stack>
      )
    }
    case COMPONENT_TYPES.enum.BULLETED_LIST:
      if (!isComponentContent(component.content) || !component.content) {
        return null
      }
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
        >
          <BulletedList componentContent={component.content} />
        </Stack>
      )
    case COMPONENT_TYPES.enum.TEXT_PARAGRAPH_V2:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
        >
          <TextParagraphV2Renderer content={component.content} />
        </Stack>
      )
    case COMPONENT_TYPES.enum.LOGO:
      return (
        <Logo
          companyName={agecData.organizationName}
          {...DesignStudioParametersToolkit.spreadContentFilteringConfiguration(
            configuration
          )}
        />
      )
    case COMPONENT_TYPES.enum.GENERIC_PASSPORT_TITLE: {
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <GenericTitle />
        </Stack>
      )
    }
    case COMPONENT_TYPES.enum.DETAILED_PRODUCT_COMPOSITION: {
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
        >
          <DetailedProductComposition agecData={agecData} />
        </Stack>
      )
    }
    case COMPONENT_TYPES.enum.PRODUCT_ATTRIBUTES:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
          py={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingBlockCSSVariable
          )}
        >
          <ProductAttributes
            {...DesignStudioParametersToolkit.spreadContentFilteringConfiguration(
              configuration
            )}
            agecData={agecData}
          />
        </Stack>
      )
    case COMPONENT_TYPES.enum.ORGANIZATION_TITLE:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
        >
          <OrganizationTitle agecData={agecData} />
        </Stack>
      )
    case COMPONENT_TYPES.enum.PRODUCT_IMAGE:
      return (
        <Stack
          px={DesignStudioParametersToolkit.getCssVariableLiteralName(
            paddingInlineCSSVariable
          )}
        >
          <ProductImageComponent image={agecData.picture} />
        </Stack>
      )
    default:
      return null
  }
}
